<template>
  <div
    v-if="debugInfo.debugPayment && !debugInfo.loading"
    class="payment-debug-info"
  >
    <h3>Debug Information</h3>
    <div class="debug-container">
      <div class="debug-info">
        <div class="title">
          Txn ID:
        </div>
        <div
          class="value copyable-value"
          @click="copyToClipboard(debugInfo.txnid)"
        >
          {{ debugInfo.txnid }}
          <span
            class="copy-icon"
            title="Copy to clipboard"
            @click.stop="copyToClipboard(debugInfo.txnid)"
          >📄</span>
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          Amount:
        </div>
        <div class="value">
          {{ debugInfo.amount | formatCurrency }}
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          Surcharge:
        </div>
        <div class="value">
          {{ debugInfo.surchargeAmount | formatCurrency }}
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          User ID:
        </div>
        <div class="value">
          {{ debugInfo.userid }}
        </div>
      </div>
      <div class="debug-info debug-info-wide">
        <div class="title">
          Payment ID:
        </div>
        <div
          class="value copyable-value"
          @click="copyToClipboard(debugInfo.paymentRequestId)"
        >
          {{ debugInfo.paymentRequestId }}
          <span
            class="copy-icon"
            title="Copy to clipboard"
            @click.stop="copyToClipboard(debugInfo.paymentRequestId)"
          >📄</span>
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          Selected Card:
        </div>
        <div class="value">
          {{ debugInfo.selectedCard }}
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          Store/Enable Credit Card:
        </div>
        <div class="value">
          {{ debugInfo.storeCreditCard }} / {{ debugInfo.enableUserCardStorage }}
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          Payment Intent Type:
        </div>
        <div class="value">
          {{ debugInfo.paymentIntentType }}
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          Customer Initiated:
        </div>
        <div class="value">
          {{ debugInfo.customerInitiated }}
        </div>
      </div>
      <div class="debug-info">
        <div class="title">
          Seller Keyed In:
        </div>
        <div class="value">
          {{ debugInfo.sellerKeyedIn }}
        </div>
      </div>
    </div>
    <div
      class="debug-verification-details debug-info"
      :class="{ expanded: showVerificationDetails }"
    >
      <div class="title">
        Verfication Details:
      </div>
      <div class="value">
        <div
          class="verification-toggle"
          @click="toggleVerificationDetails"
        >
          <span class="toggle-icon">{{ showVerificationDetails ? '▼' : '▶' }}</span>
        </div>
        <div
          v-if="showVerificationDetails"
          class="verification-content"
        >
          <pre>{{ JSON.stringify(debugInfo.verificationDetails, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'square-payments-debug-info',
  props: {
    debugInfo: {
      type: Object,
      required: true,
      default: () => ({
        txnid: '',
        amount: 0,
        paymentRequestId: '',
        userid: '',
        selectedCard: 'new',
        storeCreditCard: false,
        enableUserCardStorage: true,
        paymentIntentType: 'CHARGE',
        customerInitiated: true,
        sellerKeyedIn: false,
        verificationDetails: {},
        debugPayment: false,
        surchargeAmount: 0
      })
    }
  },
  data: function() {
    return {
      showVerificationDetails: false
    }
  },
  methods: {
    toggleVerificationDetails: function() {
      this.showVerificationDetails = !this.showVerificationDetails
    },
    copyToClipboard(value) {
      if (!value) return

      navigator.clipboard.writeText(value).then(() => {
        // Optionally show success feedback
        console.log(`Copied to clipboard: ${value}`)
      }).catch((err) => {
        // Fallback for older browsers
        console.error('Error copying to clipboard: ', err)
        // Try the fallback method
        this.fallbackCopyToClipboard(value)
      })
    },
    fallbackCopyToClipboard(value) {
      const textArea = document.createElement('textarea')
      textArea.value = value
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        console.log(`Copied to clipboard (fallback): ${value}`)
      }
      catch (err) {
        console.error('Fallback copy failed: ', err)
      }
      document.body.removeChild(textArea)
    }
  }
}
</script>

<style lang="scss" scoped>
  .payment-debug-info {
    border: 1px solid black;
    background: #eee;
    padding: 1rem;
    margin-bottom: 2rem;

    h3 {
      margin-bottom: 1rem;
    }

    .debug-info {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 0.75rem;
      border: 1px solid #ccc;
      background: white;
      border-radius: 4px;
      min-height: 3rem;
      flex: 1;
      gap: 0.75rem;

      .title {
        font-weight: bold;
        font-size: 0.875rem;
        color: #666;
        white-space: nowrap;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        flex-shrink: 0;
        min-width: fit-content;
      }

      .value {
        font-size: 1rem;
        font-weight: 500;
        word-break: break-all;
        overflow-wrap: break-word;
        line-height: 1.3;
        flex: 1;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      }
    }

    // Flexbox container for debug items
    .debug-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-bottom: 1rem;

      .debug-info {
        // Remove fixed sizing constraints - let items size naturally
        flex: 0 1 auto;
        min-width: 200px;
        max-width: 100%;

        // Wide items can still take more space when needed
        &.debug-info-wide {
          flex: 0 1 auto;
          min-width: 300px;
        }

        // Responsive breakpoints - only set minimum widths
        @media (max-width: 900px) {
          min-width: 180px;

          &.debug-info-wide {
            min-width: 250px;
          }
        }

        @media (max-width: 600px) {
          min-width: 160px;

          &.debug-info-wide {
            min-width: 160px;
          }
        }
      }
    }

    .debug-verification-details {
      display: flex;
      flex-direction: column;
      background: white;
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 0.75rem;

      .title {
        font-weight: bold;
        font-size: 0.875rem;
        color: #666;
        margin-bottom: 0.5rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: left;
      }

      .value {
        display: flex;
        flex-direction: column;

        .verification-toggle {
          cursor: pointer;
          padding: 0.5rem 0.75rem;
          background: #f8f8f8;
          border: 1px solid #ddd;
          border-radius: 4px;
          transition: background-color 0.2s ease;
          align-self: flex-start;
          display: flex;
          align-items: center;
          gap: 0.5rem;

          &:hover {
            background: #e8e8e8;
          }

          .toggle-icon {
            font-size: 0.8rem;
            transition: transform 0.2s ease;
          }
        }

        .verification-content {
          background: #f9f9f9;
          padding: 1rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          margin-top: 0.75rem;
          width: 100%;

          pre {
            margin: 0;
            font-size: 0.8rem;
            line-height: 1.4;
            white-space: pre-wrap;
            word-wrap: break-word;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          }
        }
      }
    }
  }

  // Styles for copyable values
  .copyable-value {
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: background-color 0.2s ease;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    margin: -0.25rem -0.5rem;

    &:hover {
      background-color: rgba(59, 130, 246, 0.1);
    }

    .copy-icon {
      margin-left: 0.5rem;
      font-size: 0.9rem;
      cursor: pointer;
      opacity: 0.6;
      transition: opacity 0.2s ease, transform 0.1s ease;
      flex-shrink: 0;

      &:hover {
        opacity: 1;
        transform: scale(1.1);
      }
    }
  }
</style>
